/**
 * Week calendar for visit schedule (訪問入力).
 * Displays time slots (20 min, 3 per hour) 0–23h and supports drag-drop move of visits.
 * Header fixed in box, body scrollable. RA2: today column #fafad2, header MM/DD（月）.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import type { VisitScheduleVisit } from '@/services/main/visitSchedule';
import { AppButton, AppCard } from '@/components/atoms';
import { normalizeVisitErrorMessage } from '@/components/molecules/VisitErrorMessageList';
import { WEEKDAY_LABELS_MON_FIRST } from '@/constants/weekday';
import { useJpHolidayDates } from '@/services/main/jpHolidays';
import { useMainClinicInformationList } from '@/hooks/main/useMainClinicInformation';

/** Visit for display (id may be string for drafts). */
export type VisitScheduleVisitDisplay = Omit<VisitScheduleVisit, 'id'> & {
  id: number | string;
  is_copied?: boolean;
  copy_visit_id?: number | null;
};

const SLOT_MINUTES = 20;
const HOURS_COUNT = 24;
const SLOTS_PER_HOUR = 3;
const TOTAL_SLOTS = HOURS_COUNT * SLOTS_PER_HOUR; // 72
const SLOT_HEIGHT_PX = 28; // matches Tailwind h-7 (1.75rem)

interface VisitScheduleWeekCalendarProps {
  visitdate: string;
  visits: VisitScheduleVisitDisplay[];
  checkinEnabled?: boolean;
  isCheckinEnabledForVisit?: (visit: VisitScheduleVisitDisplay) => boolean;
  /** When true, render only inner content (no AppCard) for embedding in one block with 出発位置. */
  noCard?: boolean;
  className?: string;
  /** Click on empty cell: (dateStr YYYY-MM-DD, timeStr HH:mm, dayIndex, slotIndex) */
  onCellClick?: (dateStr: string, timeStr: string, dayIndex: number, slotIndex: number) => void;
  /** Click on a visit (for edit). */
  onVisitClick?: (visit: VisitScheduleVisitDisplay) => void;
  /** Copy all visits of this day to another day (opens modal). dayStr = YYYY-MM-DD */
  onCopyDayClick?: (dayStr: string) => void;
  /** Delete all (non-locked) visits of this day. dayStr = YYYY-MM-DD */
  onDeleteDayClick?: (dayStr: string) => void;
  /** Detail view for this day. dayStr = YYYY-MM-DD */
  onDetailClick?: (dayStr: string) => void;
  /** Drag a visit onto another cell (move date/time, keep duration). */
  onVisitDrop?: (visit: VisitScheduleVisitDisplay, dateStr: string, timeStr: string) => void;
  /** Resize a visit (drag bottom edge) to change visit_minutes. Minutes snapped to SLOT_MINUTES. */
  onVisitResize?: (visit: VisitScheduleVisitDisplay, newMinutes: number) => void;
}

function getWeekRange(visitdate: string): { weekStart: dayjs.Dayjs; days: dayjs.Dayjs[] } {
  const d = dayjs(visitdate);
  const dayOfWeek = d.day(); // 0=Sun ... 6=Sat
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const weekStart = d.add(diffToMonday, 'day').startOf('day'); // Monday (same week)
  const days: dayjs.Dayjs[] = [];
  for (let i = 0; i < 7; i++) {
    days.push(weekStart.add(i, 'day'));
  }
  return { weekStart, days };
}

function getSlotBounds(
  day: dayjs.Dayjs,
  slotIndex: number
): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
  const start = day.startOf('day').add(slotIndex * SLOT_MINUTES, 'minute');
  const end = start.add(SLOT_MINUTES, 'minute');
  return { start, end };
}

function getVisitStartSlotIndex(visit: VisitScheduleVisitDisplay, day: dayjs.Dayjs): number | null {
  const visitStart = dayjs(visit.visit_datetime_from);
  if (visitStart.format('YYYY-MM-DD') !== day.format('YYYY-MM-DD')) {
    return null;
  }

  const minutesFromDayStart = visitStart.diff(day.startOf('day'), 'minute');
  if (minutesFromDayStart < 0) {
    return null;
  }

  const slotIndex = Math.floor(minutesFromDayStart / SLOT_MINUTES);
  if (slotIndex < 0 || slotIndex >= TOTAL_SLOTS) {
    return null;
  }

  return slotIndex;
}

const todayStr = dayjs().format('YYYY-MM-DD');

/** "HH:MM" / "HH:MM:SS" → minutes since 00:00, or null. */
function parseTimeToMinutes(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(mm)) return null;
  return h * 60 + mm;
}

/** "1,2,3,4,5" → Set of day-of-week numbers (0=Sun..6=Sat), or null. */
function parseOpenWeek(s: string | null | undefined): Set<number> | null {
  if (!s) return null;
  const parts = String(s)
    .split(',')
    .map(x => parseInt(x.trim(), 10))
    .filter(n => !Number.isNaN(n) && n >= 0 && n <= 6);
  return parts.length ? new Set(parts) : null;
}

function getVisitCardBgClass(visit: VisitScheduleVisitDisplay): string {
  // Red highlight is driven solely by BE-computed is_error (post-save). FE no longer
  // marks time-overlap as red during drag/drop or copy, to avoid flashing red on
  // unsaved transient states.
  if (visit.is_locked) return 'bg-[rgb(200,200,200)]';
  if (visit.is_error) return 'bg-[rgb(255,209,209)]';
  if (visit.same_visit_id) return 'bg-[rgb(133,231,176)]';
  return 'bg-[rgb(180,235,250)]';
}

interface DroppableCellProps {
  id: string;
  children: React.ReactNode;
  className: string;
  role?: string;
  tabIndex?: number;
  onClick?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTableCellElement>) => void;
}

const DroppableCell: React.FC<DroppableCellProps> = ({
  id,
  children,
  className,
  role,
  tabIndex,
  onClick,
  onKeyDown,
}) => {
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <td
      ref={setNodeRef}
      role={role}
      tabIndex={tabIndex}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={`${className} ${isOver ? 'ring-2 ring-inset ring-blue-400' : ''}`}
    >
      {children}
    </td>
  );
};

/**
 * Red × mark: visit is not eligible for transport fee (往療料).
 * Rule: not 突発的訪問 AND (通所 OR consent-derived transport exclusion).
 *
 * BE computes is_non_distance_price_visit from consent.walking_condition_id=2 or
 * consent.is_not_transport_cost=1. The direct consent fields here cover stale visit rows.
 */
function shouldShowNoTransportCostMark(visit: VisitScheduleVisitDisplay): boolean {
  if (visit.is_suddenly_visit) return false;
  return (
    Boolean(visit.is_non_visit_treatment) ||
    Boolean(visit.is_non_distance_price_visit) ||
    Boolean(visit.is_not_transport_cost) ||
    visit.walking_condition_id === 2
  );
}

interface DraggableVisitCardProps {
  draggableId: string;
  visit: VisitScheduleVisitDisplay;
  checkinEnabled?: boolean;
  isCheckinEnabledForVisit?: (visit: VisitScheduleVisitDisplay) => boolean;
  onVisitClick?: (visit: VisitScheduleVisitDisplay) => void;
  onVisitResize?: (visit: VisitScheduleVisitDisplay, newMinutes: number) => void;
  stackIndex?: number;
  /** Total number of visits in the same slot — used to split horizontal width (FullCalendar parity). */
  stackCount?: number;
}

const DraggableVisitCard: React.FC<DraggableVisitCardProps> = ({
  draggableId,
  visit,
  checkinEnabled = false,
  isCheckinEnabledForVisit,
  onVisitClick,
  onVisitResize,
  stackIndex = 0,
  stackCount = 1,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: draggableId,
    data: { visit },
  });

  const errorMsg = visit.error_messages
    ? normalizeVisitErrorMessage(undefined, String(visit.error_messages))
    : visit.error_messages;
  const hasMemo = Boolean((visit.massager_remarks ?? '').toString().trim());
  const showNoTransportCost = shouldShowNoTransportCostMark(visit);
  const hasStarted = Boolean(visit.massager_started_at);
  const hasEnded = Boolean(visit.massager_ended_at);
  const visitCheckinEnabled = isCheckinEnabledForVisit
    ? isCheckinEnabledForVisit(visit)
    : checkinEnabled;
  const showInProgress = visitCheckinEnabled && hasStarted && !hasEnded;
  const showDone = visitCheckinEnabled && hasEnded;
  const isSuddenlyVisit = Boolean(visit.is_suddenly_visit);
  const timeStr = dayjs(visit.visit_datetime_from).format('HH:mm');
  const serial = (visit.serial_number ?? '').toString().trim();
  const name = visit.patient_name || '—';
  const title = `${timeStr} - ${serial ? `${serial} ${name}` : name}`.replace(/\s+/g, ' ').trim();

  // Render proportional to actual minutes: odd-minute starts (e.g. 16:05) and non-20-min
  // durations (e.g. 10 min) must be visually accurate. 1 minute = SLOT_HEIGHT_PX / SLOT_MINUTES px.
  const pxPerMinute = SLOT_HEIGHT_PX / SLOT_MINUTES;
  const visitStartMinute = dayjs(visit.visit_datetime_from).minute() % SLOT_MINUTES;
  const topOffsetPx = visitStartMinute * pxPerMinute;
  const visitMinutes = Math.max(1, visit.visit_minutes || SLOT_MINUTES);
  const baseHeightPx = visitMinutes * pxPerMinute;

  const [previewHeightPx, setPreviewHeightPx] = useState<number | null>(null);
  const heightPx = previewHeightPx ?? baseHeightPx;

  // Resize disallowed when visit is locked or 突発的な往療 (same rules as drag-move in parent).
  const isResizable = Boolean(onVisitResize) && !visit.is_locked && !visit.is_suddenly_visit;

  const handleResizePointerDown = (e: React.PointerEvent) => {
    if (!isResizable || !onVisitResize) return;
    e.stopPropagation();
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = baseHeightPx;
    let committedMinutes = visitMinutes;

    const handleMove = (ev: PointerEvent) => {
      const delta = ev.clientY - startY;
      const proposedHeight = Math.max(SLOT_HEIGHT_PX, startHeight + delta);
      const slots = Math.max(1, Math.round(proposedHeight / SLOT_HEIGHT_PX));
      committedMinutes = slots * SLOT_MINUTES;
      setPreviewHeightPx(slots * SLOT_HEIGHT_PX);
    };

    const handleUp = () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      setPreviewHeightPx(null);
      if (committedMinutes !== visitMinutes) {
        onVisitResize(visit, committedMinutes);
      }
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  };

  return (
    <div
      ref={setNodeRef}
      onClick={
        onVisitClick
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onVisitClick(visit);
            }
          : undefined
      }
      title={title}
      className={`absolute flex flex-col overflow-hidden rounded border-l-4 px-1 py-0.5 text-xs ${
        onVisitClick ? 'cursor-pointer hover:opacity-90' : ''
      } ${getVisitCardBgClass(visit)} ${isDragging ? 'opacity-60 shadow-lg' : ''}`}
      style={{
        // FullCalendar parity: split cell width when multiple visits share the slot.
        // Events earlier in the stack sit to the left; later events shift rightward.
        top: `${1 + topOffsetPx}px`,
        left: `calc(${(stackIndex / stackCount) * 100}% + 2px)`,
        width: `calc(${100 / stackCount}% - 4px)`,
        height: `${Math.max(6, heightPx - 2)}px`,
        zIndex: 10 + stackIndex,
        transform: transform ? CSS.Translate.toString(transform) : undefined,
      }}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center">
        <span className={`min-w-0 flex-1 truncate ${isSuddenlyVisit ? 'text-blue-600' : ''}`}>
          {title}
        </span>
        <span className="ml-1 inline-flex shrink-0 gap-0.5">
          {showInProgress && (
            <i className="text-blue-600 fa fa-map-marker" aria-hidden="true" title="施術中" />
          )}
          {showDone && (
            <i className="text-blue-600 fa fa-thumbs-o-up" aria-hidden="true" title="施術完了" />
          )}
          {hasMemo && (
            <i
              className="text-blue-600 fa fa-sticky-note-o"
              aria-hidden="true"
              title="施術メモ有"
            />
          )}
          {showNoTransportCost && (
            <i
              className="font-bold text-red-600 fa fa-times"
              aria-hidden="true"
              title="往療料対象外"
            />
          )}
        </span>
      </div>
      {errorMsg && (
        <div className="mt-0.5 truncate text-[10px] text-red-600" title={errorMsg}>
          {errorMsg}
        </div>
      )}
      {isResizable && (
        <div
          className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize bg-gray-500/0 hover:bg-gray-500/30"
          onPointerDown={handleResizePointerDown}
          onClick={e => e.stopPropagation()}
          title="ドラッグして所要時間を変更"
        />
      )}
    </div>
  );
};

export const VisitScheduleWeekCalendar: React.FC<VisitScheduleWeekCalendarProps> = ({
  visitdate,
  visits,
  checkinEnabled = false,
  isCheckinEnabledForVisit,
  noCard = false,
  className = '',
  onCellClick,
  onVisitClick,
  onCopyDayClick,
  onDeleteDayClick,
  onDetailClick,
  onVisitDrop,
  onVisitResize,
}) => {
  const holidayDates = useJpHolidayDates();
  const { days } = useMemo(() => getWeekRange(visitdate), [visitdate]);

  // Business hours from clinic_informations (RA2 parity: highlight non-business cells grey).
  const { data: clinicInfoList } = useMainClinicInformationList(1);
  const businessHours = useMemo(() => {
    const info = clinicInfoList?.data?.data?.[0];
    const fromMin = parseTimeToMinutes(info?.open_time_from);
    const toMin = parseTimeToMinutes(info?.open_time_to);
    if (fromMin == null || toMin == null) return null;
    // open_week empty/null → default to all 7 days (highlight by time range only).
    const dow = parseOpenWeek(info?.open_week) ?? new Set([0, 1, 2, 3, 4, 5, 6]);
    return { fromMin, toMin, dow };
  }, [clinicInfoList]);

  const isBusinessCell = (day: dayjs.Dayjs, slotIndex: number): boolean => {
    if (!businessHours) return true;
    if (!businessHours.dow.has(day.day())) return false;
    const startMin = slotIndex * SLOT_MINUTES;
    return startMin >= businessHours.fromMin && startMin < businessHours.toMin;
  };

  // Auto-scroll to business start on mount (mimics RA2 FullCalendar `scrollTime`).
  const scrollRef = useRef<HTMLDivElement>(null);
  const didScrollRef = useRef(false);
  useEffect(() => {
    if (didScrollRef.current || !businessHours || !scrollRef.current) return;
    const slotIdx = Math.floor(businessHours.fromMin / SLOT_MINUTES);
    const SLOT_PX = 28; // matches h-7
    scrollRef.current.scrollTop = Math.max(0, slotIdx * SLOT_PX);
    didScrollRef.current = true;
  }, [businessHours]);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const visitsByDayAndSlot = useMemo(() => {
    const map: Record<string, VisitScheduleVisitDisplay[]> = {};
    days.forEach((_, dayIndex) => {
      for (let slotIndex = 0; slotIndex < TOTAL_SLOTS; slotIndex++) {
        map[`${dayIndex}-${slotIndex}`] = [];
      }
    });

    visits.forEach(visit => {
      days.forEach((day, dayIndex) => {
        const slotIndex = getVisitStartSlotIndex(visit, day);
        if (slotIndex === null) {
          return;
        }

        map[`${dayIndex}-${slotIndex}`].push(visit);
      });
    });

    return map;
  }, [days, visits]);

  /**
   * FullCalendar-style column (lane) layout per day. Per visit, resolves:
   *   - stackIndex: the column it occupies (0-based, left→right)
   *   - stackCount: total columns needed across ALL visits that overlap it in time
   * Two visits overlap iff their [from, to) intervals intersect. This lets 14:00-15:00
   * and 14:20-14:40 render side-by-side even though they start in different slots.
   */
  const visitLayoutById = useMemo(() => {
    const result = new Map<string | number, { stackIndex: number; stackCount: number }>();

    days.forEach(day => {
      const dayStr = day.format('YYYY-MM-DD');
      const dayVisits = visits
        .filter(v => (v.visit_datetime_from ?? '').slice(0, 10) === dayStr)
        .slice()
        .sort(
          (a, b) => dayjs(a.visit_datetime_from).valueOf() - dayjs(b.visit_datetime_from).valueOf()
        );

      // Greedy lane assignment: pick the first lane whose last visit already ended.
      const laneEnds: number[] = [];
      const laneByVisit = new Map<string | number, number>();
      for (const v of dayVisits) {
        const start = dayjs(v.visit_datetime_from).valueOf();
        const end = dayjs(v.visit_datetime_to).valueOf();
        let lane = laneEnds.findIndex(e => e <= start);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(end);
        } else {
          laneEnds[lane] = end;
        }
        laneByVisit.set(v.id, lane);
      }

      // stackCount = max lane index + 1 among visits that time-overlap with this one.
      for (const v of dayVisits) {
        const vStart = dayjs(v.visit_datetime_from).valueOf();
        const vEnd = dayjs(v.visit_datetime_to).valueOf();
        let maxLane = laneByVisit.get(v.id) ?? 0;
        for (const o of dayVisits) {
          if (o.id === v.id) continue;
          const oStart = dayjs(o.visit_datetime_from).valueOf();
          const oEnd = dayjs(o.visit_datetime_to).valueOf();
          if (oStart < vEnd && oEnd > vStart) {
            const ol = laneByVisit.get(o.id) ?? 0;
            if (ol > maxLane) maxLane = ol;
          }
        }
        result.set(v.id, {
          stackIndex: laneByVisit.get(v.id) ?? 0,
          stackCount: maxLane + 1,
        });
      }
    });

    return result;
  }, [days, visits]);

  const hasVisitByDay = useMemo(() => {
    return days.map(day => {
      const dayStr = day.format('YYYY-MM-DD');
      return visits.some(v => dayjs(v.visit_datetime_from).format('YYYY-MM-DD') === dayStr);
    });
  }, [days, visits]);

  const handleDragEnd = (event: DragEndEvent) => {
    const activeVisit = event.active.data.current?.visit as VisitScheduleVisitDisplay | undefined;
    const overId = typeof event.over?.id === 'string' ? event.over.id : '';

    if (!activeVisit || !overId.startsWith('cell-') || !onVisitDrop) {
      return;
    }

    const [datePart, timeStr] = overId.split('|');
    const dateStr = datePart.replace(/^cell-/, '');
    if (!dateStr || !timeStr) {
      return;
    }

    onVisitDrop(activeVisit, dateStr, timeStr);
  };

  const table = (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div ref={scrollRef} className="max-h-[calc(100vh-280px)] overflow-auto overflow-x-auto">
        {/* table-fixed: distribute columns equally regardless of event length.
            min-w ensures a reasonable per-column width (~120px); container scrolls
            horizontally when viewport is narrower. */}
        <table
          className="w-full min-w-[900px] border-separate border-spacing-0 table-fixed text-sm"
          role="grid"
        >
          <thead>
            <tr>
              <th className="sticky top-0 z-30 w-12 border-l border-r border-t border-b border-gray-200 bg-gray-50 p-1 text-center font-normal text-gray-600">
                時間
              </th>
              {days.map((day, i) => {
                const isToday = day.format('YYYY-MM-DD') === todayStr;
                const isSun = day.day() === 0;
                const isSat = day.day() === 6;
                const dayStr = day.format('YYYY-MM-DD');
                const isHoliday = holidayDates.has(dayStr);
                return (
                  <th
                    key={i}
                    className={`sticky top-0 z-30 border-r border-t border-b border-gray-200 bg-gray-50 p-1 text-center font-normal shadow-[0_1px_0_0_rgba(229,231,235,1)] ${isToday ? '!bg-[#fafad2]' : ''}`}
                  >
                    <span
                      className={`block ${
                        isSun || isHoliday
                          ? 'text-red-500'
                          : isSat
                            ? 'text-blue-600'
                            : 'text-gray-700'
                      }`}
                    >
                      {onCopyDayClick && (
                        <button
                          type="button"
                          className="mr-1 inline-flex items-center justify-center rounded p-0.5 text-gray-600 hover:bg-gray-200"
                          onClick={() => onCopyDayClick(dayStr)}
                          title="日付の訪問をコピー"
                        >
                          <i className="fa fa-files-o" aria-hidden="true" />
                        </button>
                      )}
                      {day.format('MM/DD')}（{WEEKDAY_LABELS_MON_FIRST[i]}）
                      {hasVisitByDay[i] && (
                        <i
                          className="ml-1 text-gray-600 fa fa-star"
                          aria-hidden="true"
                          title="訪問入力有"
                        />
                      )}
                    </span>
                    <div className="mt-1 flex justify-between gap-0.5">
                      <AppButton
                        type="primary"
                        size="small"
                        className="!py-0 !h-6 !text-xs flex-1"
                        onClick={() => onDetailClick?.(dayStr)}
                      >
                        <i className="fa fa-info-circle mr-0.5" aria-hidden="true" /> 詳細
                      </AppButton>
                      <AppButton
                        danger
                        size="small"
                        type="primary"
                        className="!py-0 !h-6 !text-xs flex-1"
                        onClick={() => onDeleteDayClick?.(dayStr)}
                      >
                        <i className="fa fa-trash-o mr-0.5" aria-hidden="true" /> 削除
                      </AppButton>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: HOURS_COUNT }, (_, hour) => {
              const slotBase = hour * SLOTS_PER_HOUR;
              return (
                <React.Fragment key={hour}>
                  <tr>
                    <td
                      rowSpan={SLOTS_PER_HOUR}
                      className="border-l border-r border-b border-gray-200 bg-gray-50 p-0.5 text-center align-top text-gray-600"
                    >
                      {hour}
                    </td>
                    {days.map((day, dayIndex) => {
                      const key = `${dayIndex}-${slotBase}`;
                      const cellVisits = visitsByDayAndSlot[key] ?? [];
                      const isToday = day.format('YYYY-MM-DD') === todayStr;
                      const isBiz = isBusinessCell(day, slotBase);
                      const dateStr = day.format('YYYY-MM-DD');
                      const { start } = getSlotBounds(day, slotBase);
                      const timeStr = start.format('HH:mm');
                      const droppableId = `cell-${dateStr}|${timeStr}`;
                      return (
                        <DroppableCell
                          key={key}
                          id={droppableId}
                          role={onCellClick ? 'button' : undefined}
                          tabIndex={onCellClick ? 0 : undefined}
                          onClick={
                            onCellClick
                              ? () => onCellClick(dateStr, timeStr, dayIndex, slotBase)
                              : undefined
                          }
                          onKeyDown={
                            onCellClick
                              ? e => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onCellClick(dateStr, timeStr, dayIndex, slotBase);
                                  }
                                }
                              : undefined
                          }
                          className={`relative min-w-[100px] h-7 align-top border-r border-b border-gray-200 p-0 ${!isBiz ? 'bg-[#f5f5f5]' : isToday ? 'bg-[#fafad2]' : ''} ${onCellClick ? 'cursor-pointer hover:bg-blue-50/50' : ''}`}
                        >
                          {cellVisits.map(v => {
                            const layout = visitLayoutById.get(v.id);
                            return (
                              <DraggableVisitCard
                                key={`${v.id}-${key}`}
                                draggableId={`visit-${v.id}-${key}`}
                                visit={v}
                                checkinEnabled={checkinEnabled}
                                isCheckinEnabledForVisit={isCheckinEnabledForVisit}
                                onVisitClick={onVisitClick}
                                onVisitResize={onVisitResize}
                                stackIndex={layout?.stackIndex ?? 0}
                                stackCount={layout?.stackCount ?? 1}
                              />
                            );
                          })}
                        </DroppableCell>
                      );
                    })}
                  </tr>
                  {[1, 2].map(subIndex => {
                    const slotIndex = slotBase + subIndex;
                    return (
                      <tr key={slotIndex}>
                        {days.map((day, dayIndex) => {
                          const key = `${dayIndex}-${slotIndex}`;
                          const cellVisits = visitsByDayAndSlot[key] ?? [];
                          const isToday = day.format('YYYY-MM-DD') === todayStr;
                          const isBiz = isBusinessCell(day, slotIndex);
                          const dateStr = day.format('YYYY-MM-DD');
                          const { start } = getSlotBounds(day, slotIndex);
                          const timeStr = start.format('HH:mm');
                          const droppableId = `cell-${dateStr}|${timeStr}`;
                          return (
                            <DroppableCell
                              key={key}
                              id={droppableId}
                              role={onCellClick ? 'button' : undefined}
                              tabIndex={onCellClick ? 0 : undefined}
                              onClick={
                                onCellClick
                                  ? () => onCellClick(dateStr, timeStr, dayIndex, slotIndex)
                                  : undefined
                              }
                              onKeyDown={
                                onCellClick
                                  ? e => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onCellClick(dateStr, timeStr, dayIndex, slotIndex);
                                      }
                                    }
                                  : undefined
                              }
                              className={`relative min-w-[100px] h-7 align-top border-r border-b border-gray-200 p-0 ${!isBiz ? 'bg-[#f5f5f5]' : isToday ? 'bg-[#fafad2]' : ''} ${onCellClick ? 'cursor-pointer hover:bg-blue-50/50' : ''}`}
                            >
                              {cellVisits.map(v => {
                                const layout = visitLayoutById.get(v.id);
                                return (
                                  <DraggableVisitCard
                                    key={`${v.id}-${key}`}
                                    draggableId={`visit-${v.id}-${key}`}
                                    visit={v}
                                    checkinEnabled={checkinEnabled}
                                    isCheckinEnabledForVisit={isCheckinEnabledForVisit}
                                    onVisitClick={onVisitClick}
                                    onVisitResize={onVisitResize}
                                    stackIndex={layout?.stackIndex ?? 0}
                                    stackCount={layout?.stackCount ?? 1}
                                  />
                                );
                              })}
                            </DroppableCell>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </DndContext>
  );

  const content = (
    <>
      <div className="w-full mt-2">{table}</div>
    </>
  );

  if (noCard) {
    return <div className={className}>{content}</div>;
  }

  return <AppCard className={className}>{content}</AppCard>;
};
